import React from 'react';
import { Key, CheckCircle, XCircle } from 'lucide-react';
import type { Permission } from '../../services/security/permissions.service';

interface PermissionsTableProps {
  permissions: Permission[];
  onPermissionClick: (permission: Permission) => void;
  isLoading?: boolean;
}

export const PermissionsTable: React.FC<PermissionsTableProps> = ({
  permissions,
  onPermissionClick,
  isLoading = false,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!permissions || permissions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Key className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-xs font-medium text-gray-900 mb-2">No hay permisos</h3>
          <p className="text-gray-500 max-w-md">
            No se encontraron permisos que coincidan con los criterios de búsqueda.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permiso
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Última Actualización
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(permissions || []).map((permission) => (
              <tr 
                key={permission.id} 
                onClick={() => onPermissionClick(permission)}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 flex items-center justify-center h-10 w-10">
                      <Key className="h-5 w-5 text-purple-500" />
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-900">
                        {permission.nombre}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {permission.id}
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="px-6 py-4">
                  <div className="text-xs text-gray-900 max-w-xs">
                    {permission.descripcion || (
                      <span className="text-gray-400 italic">Sin descripción</span>
                    )}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(permission.activo)}`}>
                      {permission.activo ? (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Activo
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactivo
                        </>
                      )}
                    </span>
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-xs text-gray-900">
                    {formatDate(permission.actualizadoEn)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};